import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useMainPageTitle } from "../../contexts/PageContext";
import { useAuth } from "../../contexts/authContext";
import ProfileImage from "../../components/profileImage";
import { getGenderDisplay } from "../../components/organization/team/GetGenderDisplay";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

const MyTeam = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updatePageTitle } = useMainPageTitle();
  const { user } = useAuth();
  
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    updatePageTitle(t("player.myTeam"), t("player.viewTeamInfo"));
  }, [updatePageTitle, t]);

  useEffect(() => {
    const fetchTeamData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error(t("errors.noAuthToken"));
        }

        const response = await fetch(`${API_URL}/team/show-my-team-for-player`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || t("player.failedToFetchTeamData"));
        }

        const data = await response.json();
        console.log("Team data:", data);
        
        // Transform the API response to match component expectations
        const transformedData = {
          team: data.team,
          teamMembers: data.team?.members || [],
          organization: data.team?.organization || null
        };
        
        console.log("Transformed data:", transformedData);
        setTeamData(transformedData);
      } catch (err) {
        console.error("Error fetching team data:", err);
        setError(err.message || t("player.failedToLoadTeamData"));
      } finally {
        setLoading(false);
      }
    };

    if (user?.metaData?.team) {
      fetchTeamData();
    } else {
      // User has no team - redirect to team search
      navigate("/player/team-search");
    }
  }, [user, navigate, t]);



  const handleUserChatClick = (userId, userName) => {
    navigate(`/chat/${userId}`, { 
      state: { 
        chatType: 'user', 
        userName: userName 
      } 
    });
  };

  const handleOrganizationChatClick = (orgId, orgName) => {
    navigate(`/chat/${orgId}`, { 
      state: { 
        chatType: 'organization', 
        organizationName: orgName 
      } 
    });
  };

  const handleUserFeedProfileClick = (userId) => {
    navigate(`/feed/${userId}`);
  };

  const handleOrganizationFeedProfileClick = (orgId) => {
    navigate(`/feed/${orgId}`);
  };



  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="px-6 py-4" style={{background: 'linear-gradient(to right, #E26735, #D45420)'}}>
              <h2 className="text-xl font-bold text-white text-center">
                {t("player.loadingYourTeam")}
              </h2>
            </div>
            <div className="p-8 text-center">
              <svg className="animate-spin h-8 w-8 text-orange-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-gray-600 mt-4">{t("player.fetchingTeamInfo")}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="px-6 py-4 bg-red-500">
              <h2 className="text-xl font-bold text-white text-center">
                {t("player.errorLoadingTeam")}
              </h2>
            </div>
            <div className="p-8 text-center">
              <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={() => navigate("/player/team-search")}
                className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                {t("player.searchForTeams")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!teamData?.team) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="px-6 py-4 bg-orange-500">
              <h2 className="text-xl font-bold text-white text-center">
                {t("player.noTeamData")}
              </h2>
            </div>
            <div className="p-8 text-center">
              <p className="text-gray-600 mb-4">{t("player.noTeamInfoFound")}</p>
              <button
                onClick={() => navigate("/player/team-search")}
                className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                {t("player.searchForTeams")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { team, teamMembers = [], organization } = teamData;
  console.log("Component received teamMembers:", teamMembers);
  
  const players = teamMembers.filter(member => member.type === 'player');
  const staff = teamMembers.filter(member => member.type === 'supervisor');
  
  console.log("Filtered players:", players);
  console.log("Filtered staff:", staff);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t("player.myTeam")}</h1>
          <p className="text-lg text-gray-600">{t("player.teamInfoAndTeammates")}</p>
        </div>

        {/* Team Info Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 mb-8">
          <div className="px-6 py-4" style={{background: 'linear-gradient(to right, #E26735, #D45420)'}}>
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">{team.name}</h2>
            </div>
          </div>

          <div className="p-6">
            <div className="flex items-start space-x-6">
              {/* Team Logo */}
              <div className="flex-shrink-0">
                <ProfileImage
                  image={team.logo}
                  alt={team.name}
                  className="w-24 h-24 rounded-xl object-cover shadow-lg"
                />
              </div>

              {/* Team Details */}
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">{t("player.uniqueName")}</h3>
                  <p className="text-lg font-semibold text-gray-900">@{team.uniqueName}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">{t("player.sport")}</h3>
                  <p className="text-lg font-semibold text-gray-900">{team.sport}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">{t("player.type")}</h3>
                  <p className="text-lg font-semibold text-gray-900">{team.type}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">{t("teams.gender")}</h3>
                  <div className="text-lg font-semibold text-gray-900">
                    {getGenderDisplay(team.gender)}
                  </div>
                </div>
              </div>
            </div>

            {/* Team Stats */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-blue-600">{players.length}</div>
                <div className="text-sm font-medium text-blue-800">{t("player.players")}</div>
              </div>
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-green-600">{staff.length}</div>
                <div className="text-sm font-medium text-green-800">{t("player.staff")}</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <div className="text-3xl font-bold text-purple-600">{teamMembers.length}</div>
                <div className="text-sm font-medium text-purple-800">{t("player.totalMembers")}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Organization Card */}
        {organization && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100 mb-8">
            <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">{t("player.organization")}</h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleOrganizationFeedProfileClick(organization._id)}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-1 rounded-lg transition-all duration-200 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>{t("player.feedProfile")}</span>
                  </button>
                  <button
                    onClick={() => handleOrganizationChatClick(organization._id, organization.name)}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white px-3 py-1 rounded-lg transition-all duration-200 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span>{t("common.chat")}</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-center space-x-4">
                <ProfileImage
                  image={organization.logo || organization.profilePicture}
                  alt={organization.name}
                  className="w-16 h-16 rounded-xl object-cover shadow-lg"
                />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{organization.name}</h3>
                  {organization.userName && (
                    <p className="text-gray-600">@{organization.userName}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team Members */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Players Section */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="px-6 py-4 bg-gradient-to-r from-green-500 to-green-600">
              <h2 className="text-xl font-bold text-white">{t("player.players")} ({players.length})</h2>
            </div>
            <div className="p-6">
              {players.length === 0 ? (
                <p className="text-gray-500 text-center py-8">{t("player.noPlayersFound")}</p>
              ) : (
                <div className="space-y-4">
                  {players.map((player) => {
                    const isCurrentUser = player.userId === user?.metaData?.userId || player.userId === user?.id || player.userId === user?._id;
                    console.log('Player check:', {
                      playerUserId: player.userId,
                      userMetaDataUserId: user?.metaData?.userId,
                      userId: user?.id,
                      userIdUnderscore: user?._id,
                      isCurrentUser
                    });
                    return (
                      <div key={player.userId} className={`flex items-center space-x-4 p-4 rounded-xl transition-colors ${
                        isCurrentUser ? 'bg-green-50 border-2 border-green-200' : 'bg-gray-50 hover:bg-gray-100'
                      }`}>
                        <ProfileImage
                          image={player.profilePicture}
                          alt={player.userName}
                          className={`w-12 h-12 rounded-full object-cover shadow ${
                            isCurrentUser ? 'ring-2 ring-green-400' : ''
                          }`}
                        />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">
                            {player.userFirstName && player.userLastName 
                              ? `${player.userFirstName} ${player.userLastName}`
                              : player.userName}
                            {isCurrentUser && (
                              <span className="text-green-600 text-sm ml-2">({t("player.you")})</span>
                            )}
                          </h3>
                          <p className="text-sm text-gray-600">@{player.userName}</p>
                          {player.role?.name && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                              {player.role.name}
                            </span>
                          )}
                        </div>
                      <div className="flex items-center space-x-3">
                        <div className="text-right">
                          <p className="text-xs text-gray-500">
                            {t("player.joined")} {new Date(player.joinedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleUserFeedProfileClick(player.userId)}
                            className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-all duration-200 flex items-center justify-center"
                            title={`${t("player.viewFeedProfile")} ${player.userName}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </button>
                          {!isCurrentUser && (
                            <button
                              onClick={() => handleUserChatClick(player.userId, player.userName)}
                              className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-lg transition-all duration-200 flex items-center justify-center"
                              title={`${t("player.chatWith")} ${player.userName}`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Staff Section */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div className="px-6 py-4 bg-gradient-to-r from-purple-500 to-purple-600">
              <h2 className="text-xl font-bold text-white">{t("player.staff")} ({staff.length})</h2>
            </div>
            <div className="p-6">
              {staff.length === 0 ? (
                <p className="text-gray-500 text-center py-8">{t("player.noStaffFound")}</p>
              ) : (
                <div className="space-y-4">
                  {staff.map((member) => (
                    <div key={member.userId} className="flex items-center space-x-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                      <ProfileImage
                        image={member.profilePicture}
                        alt={member.userName}
                        user={{
                          firstName: member.userFirstName,
                          lastName: member.userLastName,
                          userName: member.userName,
                          profilePicture: member.profilePicture
                        }}
                        className="w-12 h-12 rounded-full object-cover shadow"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {member.userFirstName && member.userLastName 
                            ? `${member.userFirstName} ${member.userLastName}`
                            : member.userName}
                        </h3>
                        <p className="text-sm text-gray-600">@{member.userName}</p>
                        {member.role?.name && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 mt-1">
                            {member.role.name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="text-right">
                          <p className="text-xs text-gray-500">
                            {t("player.joined")} {new Date(member.joinedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleUserChatClick(member.userId, member.userName)}
                            className="bg-purple-500 hover:bg-purple-600 text-white p-2 rounded-lg transition-all duration-200 flex items-center justify-center"
                            title={`${t("player.chatWith")} ${member.userName}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyTeam; 